
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Guard, Team } from "../../types";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useTeams } from "@/pages/applications/hooks/useTeams";

// Helper function to validate UUID format
const isValidUUID = (uuid: string): boolean => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(uuid);
};

export const useTeamManagement = (guard: Guard, onTeamChange?: () => void) => {
  const { toast } = useToast();
  const [guardTeams, setGuardTeams] = useState<Team[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingTeamAction, setPendingTeamAction] = useState<{
    teamId: string;
    teamName: string;
    isAssign: boolean;
  } | null>(null);
  
  const { isAdmin } = useUserRoles();
  const { user } = useSupabaseUser();
  
  // Use the centralized useTeams hook to get all available teams
  const { data: teams = [] } = useTeams();

  const fetchGuardTeams = async () => {
    setIsLoading(true);
    try {
      // Validate guard ID before proceeding
      if (!guard.id || !isValidUUID(guard.id)) {
        console.error('Invalid guard ID:', guard.id);
        setGuardTeams([]);
        setIsLoading(false);
        return;
      }
      
      console.log('Fetching teams for guard:', guard.id);
      
      // Using a direct query that respects RLS policies
      const { data, error } = await supabase
        .from('team_members')
        .select('team_id, teams:team_id(id, name, description, status, created_at, updated_at)')
        .eq('user_id', guard.id);

      if (error) {
        console.error('Error fetching guard teams:', error);
        setGuardTeams([]);
        return;
      }

      console.log('Team members data:', data);

      // Extract the team data from the nested structure and filter out any null values
      const guardTeamsData = data
        .filter(item => item.teams)
        .map(item => item.teams as Team);

      console.log('Processed guard teams:', guardTeamsData);
      setGuardTeams(guardTeamsData);
    } catch (error) {
      console.error('Error in fetchGuardTeams:', error);
      setGuardTeams([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTeamAction = async (teamId: string) => {
    // Only allow team actions for admins
    if (!isAdmin) {
      console.log("Only admins can modify team assignments");
      return;
    }
    
    const team = teams.find(t => t.id === teamId);
    if (!team) return;

    // Check current assignment status
    const isAssigned = guardTeams.some(t => t.id === teamId);

    setPendingTeamAction({
      teamId,
      teamName: team.name,
      isAssign: !isAssigned
    });
    setShowConfirmDialog(true);
  };

  const handleConfirmAction = async () => {
    if (!pendingTeamAction || !isAdmin) return;
    
    // Validate guard ID before proceeding
    if (!guard.id || !isValidUUID(guard.id)) {
      console.error('Invalid guard ID:', guard.id);
      toast({
        title: "Error",
        description: "Invalid guard ID. Please reload and try again.",
        variant: "destructive",
      });
      setShowConfirmDialog(false);
      setPendingTeamAction(null);
      return;
    }

    const { teamId, isAssign } = pendingTeamAction;

    try {
      if (isAssign) {
        // Insert team member
        const { error } = await supabase
          .from('team_members')
          .insert({
            team_id: teamId,
            user_id: guard.id
          });

        if (error) throw error;

        toast({
          title: "Team Assigned",
          description: "The guard has been successfully added to the team.",
        });

        // Trigger parent refresh
        if (onTeamChange) onTeamChange();
        
        // Refresh teams immediately to show the update
        await fetchGuardTeams();
      } else {
        const { error } = await supabase
          .from('team_members')
          .delete()
          .eq('team_id', teamId)
          .eq('user_id', guard.id);

        if (error) throw error;

        toast({
          title: "Team Unassigned",
          description: "The guard has been successfully removed from the team.",
        });

        // Trigger parent refresh
        if (onTeamChange) onTeamChange();
        
        // Refresh teams immediately to show the update
        await fetchGuardTeams();
      }
    } catch (error: any) {
      console.error('Error managing team:', error);
      toast({
        title: "Error",
        description: `Failed to ${isAssign ? 'add' : 'remove'} team. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setShowConfirmDialog(false);
      setPendingTeamAction(null);
      // Make sure we have the latest data from the database
      await fetchGuardTeams();
    }
  };

  useEffect(() => {
    if (guard?.id && isValidUUID(guard.id)) {
      fetchGuardTeams();
    } else {
      setGuardTeams([]);
      setIsLoading(false);
    }
  }, [guard?.id]);

  return {
    teams,
    guardTeams,
    isLoading,
    showConfirmDialog,
    pendingTeamAction,
    handleTeamAction,
    handleConfirmAction,
    setShowConfirmDialog,
    setPendingTeamAction
  };
};
